import { LoadingRow } from '@/core/ui/components/Loading';
import React, { useState, useEffect } from 'react';
import { Camera, Plus, Trash2, Edit2, Save, X, ShieldCheck, ShieldAlert, Eye, List, Car } from 'lucide-react';
import { useI18n } from '@/shared/i18n';
import { useServices, useToast, getErrorMessage, useDeleteConfirmation } from '@/app/providers';
import { useAbility } from '@/modules/identity/application/ability';
import { LicensePlate, PlateStatus } from '@/shared/types';
import { Button } from '../atoms/Button';
import { Input } from '../atoms/Input';

export const AdminLPRList = () => {
  const { t } = useI18n();
  const ability = useAbility();
  const toast = useToast();
  const { confirmDelete } = useDeleteConfirmation();
  const { licensePlateRepo } = useServices();

  if (!ability.can('manage', 'AccessDevice') && !ability.can('read', 'AccessDevice')) {
    return (
      <div className="flex items-center justify-center p-12 text-slate-400">
        No tienes permisos para ver esta sección.
      </div>
    );
  }

  const canManage = ability.can('manage', 'AccessDevice');
  
  const [activeTab, setActiveTab] = useState<'LIVE' | 'MANAGE'>('MANAGE');
  const [plates, setPlates] = useState<LicensePlate[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Form State
  const [editingPlate, setEditingPlate] = useState<Partial<LicensePlate>>({
    status: PlateStatus.WHITELIST,
    number: '',
    alias: '',
    notes: ''
  });
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    fetchPlates();
  }, []);

  const fetchPlates = async () => {
    setLoading(true);
    try {
      const data = await licensePlateRepo.getAllPlates();
      setPlates(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    if (!canManage) return;
    setEditingPlate({
      status: PlateStatus.WHITELIST,
      number: '',
      alias: '',
      notes: ''
    });
    setIsEditMode(false);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (plate: LicensePlate) => {
    if (!canManage) return;
    setEditingPlate({ ...plate });
    setIsEditMode(true);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string, plateNumber: string) => {
    if (!canManage) return;
    await confirmDelete({
      itemName: plateNumber,
      itemType: 'Placa',
      onConfirm: async () => {
        await licensePlateRepo.deletePlate(id);
        fetchPlates();
      }
    });
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canManage) return;
    if (!editingPlate.number) return;

    try {
      // Ensure uppercase plate
      const payload = { ...editingPlate, number: editingPlate.number.toUpperCase() };

      if (isEditMode && editingPlate.id) {
        await licensePlateRepo.updatePlate(payload as LicensePlate);
      } else {
        await licensePlateRepo.createPlate(payload as any);
      }
      setIsModalOpen(false);
      fetchPlates();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  return (
    <>
      <div className="space-y-6 animate-in fade-in duration-300">
        
        {/* Header Tabs */}
        <div className="bg-white rounded-xl p-2 shadow-sm border border-slate-100 flex gap-2 w-fit">
           <button 
             onClick={() => setActiveTab('MANAGE')}
             className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-all ${activeTab === 'MANAGE' ? 'bg-slate-900 text-white shadow' : 'text-slate-500 hover:bg-slate-50'}`}
           >
             <List size={16}/> {t('admin.lpr.tab.manage')}
           </button>
           <button 
             onClick={() => setActiveTab('LIVE')}
             className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-all ${activeTab === 'LIVE' ? 'bg-slate-900 text-white shadow' : 'text-slate-500 hover:bg-slate-50'}`}
           >
             <Eye size={16}/> {t('admin.lpr.tab.live')}
           </button>
        </div>

        {activeTab === 'LIVE' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Simulated Camera Feed 1 */}
             <div className="bg-black rounded-xl overflow-hidden relative aspect-video shadow-lg group">
                <div className="absolute top-4 left-4 bg-red-600 text-white text-xs px-2 py-1 rounded animate-pulse font-bold flex items-center gap-1">
                   <div className="w-2 h-2 bg-white rounded-full"></div> LIVE
                </div>
                <div className="absolute top-4 right-4 text-white/70 text-xs font-mono">CAM-01 [ENTRADA]</div>
                
                {/* Simulated Content */}
                <div className="w-full h-full flex items-center justify-center bg-slate-800">
                   <Car className="text-slate-700 w-24 h-24 opacity-50" />
                </div>

                {/* Recognition Overlay (Simulated) */}
                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 bg-white/90 backdrop-blur px-4 py-2 rounded-lg shadow-lg flex items-center gap-3 border-2 border-emerald-500">
                   <div className="text-center">
                      <p className="text-[10px] text-slate-500 font-bold uppercase">Placa Detectada</p>
                      <p className="text-2xl font-mono font-bold text-slate-900 tracking-widest">XYZ-987</p>
                   </div>
                   <div className="h-8 w-px bg-slate-300"></div>
                   <div>
                      <span className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-100 px-2 py-1 rounded text-xs font-bold">
                        <ShieldCheck size={12}/> Permitido
                      </span>
                   </div>
                </div>
             </div>

             {/* Simulated Camera Feed 2 */}
             <div className="bg-black rounded-xl overflow-hidden relative aspect-video shadow-lg">
                <div className="absolute top-4 left-4 bg-red-600 text-white text-xs px-2 py-1 rounded animate-pulse font-bold flex items-center gap-1">
                   <div className="w-2 h-2 bg-white rounded-full"></div> LIVE
                </div>
                <div className="absolute top-4 right-4 text-white/70 text-xs font-mono">CAM-02 [SALIDA]</div>
                <div className="w-full h-full flex items-center justify-center bg-slate-800">
                   <div className="text-slate-600 text-sm">Sin movimiento</div>
                </div>
             </div>
          </div>
        )}

        {activeTab === 'MANAGE' && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center">
              <h3 className="font-bold text-slate-800 text-lg">{t('admin.lpr.title')}</h3>
              {canManage && (
                <Button onClick={handleOpenCreate} icon={<Plus size={16}/>}>
                  Registrar Placa
                </Button>
              )}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-slate-50 text-slate-500 font-medium">
                  <tr>
                    <th className="p-4">{t('admin.lpr.table.plate')}</th>
                    <th className="p-4">{t('admin.lpr.table.alias')}</th>
                    <th className="p-4">{t('admin.lpr.table.status')}</th>
                    <th className="p-4 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading ? (
                     <LoadingRow colSpan={4} />
                  ) : plates.length === 0 ? (
                     <tr><td colSpan={4} className="p-8 text-center text-slate-400">No hay placas registradas.</td></tr>
                  ) : (
                    plates.map((plate) => (
                      <tr key={plate.id} className="hover:bg-slate-50 transition-colors">
                        <td className="p-4 font-mono font-bold text-lg text-slate-800">
                          {plate.number}
                        </td>
                        <td className="p-4 text-slate-600">
                          <p className="font-medium text-slate-800">{plate.alias}</p>
                          {plate.notes && <p className="text-xs text-slate-400 mt-0.5">{plate.notes}</p>}
                        </td>
                        <td className="p-4">
                          {plate.status === PlateStatus.WHITELIST ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                              <ShieldCheck size={12}/> {t('admin.lpr.status.whitelist')}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-red-50 text-red-700 border border-red-200">
                              <ShieldAlert size={12}/> {t('admin.lpr.status.blacklist')}
                            </span>
                          )}
                        </td>
                        <td className="p-4 text-right">
                          {canManage && (
                            <div className="flex justify-end gap-2">
                              <button onClick={() => handleOpenEdit(plate)} className="p-1.5 text-slate-400 hover:text-primary-600 rounded transition-colors">
                                <Edit2 size={16} />
                              </button>
                              <button onClick={() => handleDelete(plate.id, plate.number)} className="p-1.5 text-slate-400 hover:text-red-600 rounded transition-colors">
                                <Trash2 size={16} />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center p-5 border-b border-slate-100">
              <h3 className="font-bold text-slate-800 text-lg">{isEditMode ? t('common.edit') : t('admin.lpr.modal.title')}</h3>
              <button onClick={() => setIsModalOpen(false)}><X size={20} className="text-slate-400"/></button>
            </div>
            
            <form onSubmit={handleSave} className="p-6 space-y-4">
              
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 text-center mb-2">
                 <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">{t('admin.lpr.modal.plate')}</label>
                 <input 
                   className="w-full text-center text-3xl font-mono font-bold bg-transparent border-none focus:ring-0 placeholder-slate-300 uppercase tracking-widest text-slate-800"
                   placeholder="XXX-000"
                   value={editingPlate.number || ''}
                   onChange={e => setEditingPlate({...editingPlate, number: e.target.value.toUpperCase()})}
                   required
                   maxLength={10}
                 />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('admin.lpr.table.status')}</label>
                <div className="grid grid-cols-2 gap-3">
                   <button
                     type="button"
                     onClick={() => setEditingPlate({...editingPlate, status: PlateStatus.WHITELIST})}
                     className={`p-3 rounded-lg border flex flex-col items-center gap-2 transition-all ${
                       editingPlate.status === PlateStatus.WHITELIST
                       ? 'border-emerald-500 bg-emerald-50 text-emerald-700 font-bold' 
                       : 'border-slate-200 text-slate-500 hover:bg-slate-50'
                     }`}
                   >
                     <ShieldCheck size={20} />
                     {t('admin.lpr.status.whitelist')}
                   </button>
                   <button
                     type="button"
                     onClick={() => setEditingPlate({...editingPlate, status: PlateStatus.BLACKLIST})}
                     className={`p-3 rounded-lg border flex flex-col items-center gap-2 transition-all ${
                       editingPlate.status === PlateStatus.BLACKLIST
                       ? 'border-red-500 bg-red-50 text-red-700 font-bold' 
                       : 'border-slate-200 text-slate-500 hover:bg-slate-50'
                     }`}
                   >
                     <ShieldAlert size={20} />
                     {t('admin.lpr.status.blacklist')}
                   </button>
                </div>
              </div>

              <Input 
                label={t('admin.lpr.modal.alias')}
                value={editingPlate.alias || ''} 
                onChange={e => setEditingPlate({...editingPlate, alias: e.target.value})}
                placeholder="Ej. Roberto Gómez (Torre A-402)"
                required
              />

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('admin.lpr.modal.notes')}</label>
                <textarea 
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-100"
                  rows={2}
                  value={editingPlate.notes || ''}
                  onChange={e => setEditingPlate({...editingPlate, notes: e.target.value})}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="secondary" className="flex-1" onClick={() => setIsModalOpen(false)}>
                  {t('common.close')}
                </Button>
                <Button type="submit" variant="primary" className="flex-1" icon={<Save size={16}/>}>
                  {t('common.save')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};